import "@/index.css";
import { lazy, Suspense } from "react";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "@/contexts/AuthContext";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import ProtectedRoute from "@/components/ProtectedRoute";
import HomePage from "@/pages/Home";
import DirectoryPage from "@/pages/Directory";
import ListingDetailPage from "@/pages/ListingDetail";
import LoginPage from "@/pages/Login";
import SignupPage from "@/pages/Signup";
import AuthCallback from "@/pages/AuthCallback";
import DashboardPage from "@/pages/Dashboard";
import NewListingPage from "@/pages/NewListing";
import ManageListingPage from "@/pages/ManageListing";
import BlogEditorPage from "@/pages/BlogEditor";
import BlogIndexPage from "@/pages/BlogIndex";
import BlogPostPage from "@/pages/BlogPost";
import ClaimListingPage from "@/pages/ClaimListing";
import MyAdsPage from "@/pages/MyAds";
import NewAdPage from "@/pages/AdNew";
import AdDetailPage from "@/pages/AdDetail";
import PreferencesPage from "@/pages/Preferences";
import { ConfirmSubscriptionPage, UnsubscribePage } from "@/pages/EmailPages";
import AboutPage from "@/pages/About";
import HelpPage from "@/pages/Help";
// Code-split all admin routes so public visitors never load the admin bundle
const AdminLayout = lazy(() => import("@/components/AdminLayout"));
const AdminImportPage = lazy(() => import("@/pages/AdminImport"));
const AdminClaimsPage = lazy(() => import("@/pages/AdminClaims"));
const AdminSubmissionsPage = lazy(() => import("@/pages/AdminSubmissions"));
const AdminBlogPage = lazy(() => import("@/pages/AdminBlog"));
const AdminAdsPage = lazy(() => import("@/pages/AdminAds"));
const AdminPayoutsPage = lazy(() => import("@/pages/AdminPayouts"));
const AdminNewslettersPage = lazy(() => import("@/pages/AdminNewsletters"));
const AdminEmailSettingsPage = lazy(() => import("@/pages/AdminEmailSettings"));
const AdminEmailLogsPage = lazy(() => import("@/pages/AdminEmailLogs"));
const AdminKpiPage = lazy(() => import("@/pages/AdminKpi"));
const AdminStakeholdersPage = lazy(() => import("@/pages/AdminStakeholders"));
const AdminUsersPage = lazy(() => import("@/pages/AdminUsers"));
const AdminCategoriesPage = lazy(() => import("@/pages/AdminCategories"));
const AdminListingsPage = lazy(() => import("@/pages/AdminListings"));
const AdminDuplicatesPage = lazy(() => import("@/pages/AdminDuplicates"));

function NotFound() {
  return (
    <div className="container-page py-24 text-center">
      <span className="chip">404</span>
      <h1 className="mt-4 font-display text-5xl font-bold text-primary">Page not found</h1>
      <p className="mt-3 text-muted-foreground">Let's get you back on track.</p>
      <a href="/" className="btn-primary mt-6 inline-flex">Back home</a>
    </div>
  );
}

function Shell({ children }) {
  return (
    <div className="flex min-h-screen flex-col">
      <Header />
      <main className="flex-1">{children}</main>
      <Footer />
    </div>
  );
}

const wrap = (el) => <Shell>{el}</Shell>;
const wrapProtected = (el, roles) => <Shell><ProtectedRoute roles={roles}>{el}</ProtectedRoute></Shell>;
const ADMIN_ROLES = ["admin", "manager"];
const AdminSuspense = ({ children }) => (
  <Suspense fallback={<div className="grid min-h-[60vh] place-items-center text-muted-foreground">Loading admin…</div>}>
    {children}
  </Suspense>
);
const wrapAdmin = (el, roles = ADMIN_ROLES) => (
  <Shell><ProtectedRoute roles={roles}><AdminSuspense><AdminLayout>{el}</AdminLayout></AdminSuspense></ProtectedRoute></Shell>
);

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Toaster position="top-center" richColors closeButton />
        <Routes>
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route path="/" element={wrap(<HomePage />)} />
          <Route path="/directory" element={wrap(<DirectoryPage />)} />
          <Route path="/listing/:id" element={wrap(<ListingDetailPage />)} />
          <Route path="/listing/:id/claim" element={wrapProtected(<ClaimListingPage />)} />
          <Route path="/login" element={wrap(<LoginPage />)} />
          <Route path="/signup" element={wrap(<SignupPage />)} />
          <Route path="/blog" element={wrap(<BlogIndexPage />)} />
          <Route path="/blog/:slug" element={wrap(<BlogPostPage />)} />
          <Route path="/dashboard" element={wrapProtected(<DashboardPage />)} />
          <Route path="/dashboard/listings/new" element={wrapProtected(<NewListingPage />)} />
          <Route path="/dashboard/listings/:id" element={wrapProtected(<ManageListingPage />)} />
          <Route path="/dashboard/listings/:id/blog/new" element={wrapProtected(<BlogEditorPage />)} />
          <Route path="/dashboard/listings/:id/blog/edit/:blogId" element={wrapProtected(<BlogEditorPage />)} />
          <Route path="/dashboard/ads" element={wrapProtected(<MyAdsPage />)} />
          <Route path="/dashboard/ads/new" element={wrapProtected(<NewAdPage />)} />
          <Route path="/dashboard/ads/:id" element={wrapProtected(<AdDetailPage />)} />
          <Route path="/about" element={wrap(<AboutPage />)} />
          <Route path="/admin" element={wrapAdmin(<AdminKpiPage />)} />
          <Route path="/admin/import" element={wrapAdmin(<AdminImportPage />, ["admin"])} />
          <Route path="/admin/listings" element={wrapAdmin(<AdminListingsPage />)} />
          <Route path="/admin/duplicates" element={wrapAdmin(<AdminDuplicatesPage />, ["admin"])} />
          <Route path="/admin/claims" element={wrapAdmin(<AdminClaimsPage />)} />
          <Route path="/admin/submissions" element={wrapAdmin(<AdminSubmissionsPage />)} />
          <Route path="/admin/blog" element={wrapAdmin(<AdminBlogPage />)} />
          <Route path="/admin/blog/edit/:blogId" element={wrapAdmin(<BlogEditorPage />)} />
          <Route path="/admin/ads" element={wrapAdmin(<AdminAdsPage />)} />
          <Route path="/admin/payouts" element={wrapAdmin(<AdminPayoutsPage />, ["admin"])} />
          <Route path="/admin/newsletters" element={wrapAdmin(<AdminNewslettersPage />)} />
          <Route path="/admin/email-settings" element={wrapAdmin(<AdminEmailSettingsPage />)} />
          <Route path="/admin/email-logs" element={wrapAdmin(<AdminEmailLogsPage />)} />
          <Route path="/admin/users" element={wrapAdmin(<AdminUsersPage />, ["admin"])} />
          <Route path="/admin/categories" element={wrapAdmin(<AdminCategoriesPage />, ["admin"])} />
          <Route path="/admin/stakeholders" element={wrapAdmin(<AdminStakeholdersPage />, ["admin"])} />
          <Route path="/admin/help" element={wrapAdmin(<HelpPage />)} />
          <Route path="/dashboard/preferences" element={wrapProtected(<PreferencesPage />)} />
          <Route path="/confirm-subscription" element={<ConfirmSubscriptionPage />} />
          <Route path="/unsubscribe" element={<UnsubscribePage />} />
          <Route path="*" element={wrap(<NotFound />)} />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}
